function pxToRem(value: number) {
  return `${value / 16}rem`
}

function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  }
}

// ----------------------------------------------------------------------

/**
 * DM Sans is the Trustmary brand sans (same family as trustmary.com). The mono is
 * for data — counts, dates, IDs, metrics — so numbers read as numbers and align in
 * columns regardless of DM Sans' own figure support. Both are loaded in the app's
 * index.html; the system stack stays as the fallback while the webfont loads.
 */
const FONT_PRIMARY =
  "'DM Sans', -apple-system, BlinkMacSystemFont, avenir next, avenir, segoe ui, helvetica neue, helvetica, Cantarell, Ubuntu, roboto, noto, arial, sans-serif"
const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

/**
 * Six sizes, hierarchy by weight. The scale is deliberately short: every new
 * one-off size makes the UI harder to keep coherent, so reach for a weight or a
 * colour token before adding a step here.
 *   h4 21 · pageTitle/h5/h6/subtitle1 16 · body1/base 14 · body2/caption 12.5 · overline 11
 * The 14px base is a density decision: many users run small screens, and the
 * lab's benchmark apps run at 13–14. h1–h3 stay large for marketing-ish
 * surfaces (onboarding, auth) only.
 */
const typography = {
  fontFamily: FONT_PRIMARY,
  fontFamilyMono: FONT_MONO,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    fontWeight: 600,
    lineHeight: 80 / 64,
    fontSize: pxToRem(40),
    letterSpacing: '-0.02em',
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },
  h2: {
    fontWeight: 600,
    lineHeight: 56 / 48,
    fontSize: pxToRem(32),
    letterSpacing: '-0.015em',
    ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
  },
  h3: {
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(24),
    letterSpacing: '-0.01em',
    ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
  },
  h4: {
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(21),
    letterSpacing: '-0.005em',
  },
  h5: {
    fontWeight: 600,
    lineHeight: 1.4,
    fontSize: pxToRem(16),
    letterSpacing: '-0.005em',
  },
  h6: {
    fontWeight: 600,
    lineHeight: 28 / 18,
    fontSize: pxToRem(16),
    letterSpacing: '-0.005em',
  },
  /**
   * The heading at the top of an app view (Reviews, Responses, a survey name).
   * Replaces the hardcoded `fontFamily="Instrument Serif"` that was copied across
   * nine call sites: the serif belongs to the marketing site, the app stays on
   * one family and gets its hierarchy from weight.
   */
  pageTitle: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
    letterSpacing: '-0.005em',
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(14),
  },
  body2: {
    lineHeight: 1.5,
    fontSize: pxToRem(12.5),
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12.5),
  },
  overline: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(11),
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: 600,
    lineHeight: 24 / 13,
    fontSize: pxToRem(13),
    textTransform: 'none',
  },
} as const

export default typography
